import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

export const DiscreteAlertsTab = ({
  discreteAlerts,
  selectedDiscreteAlerts,
  setSelectedDiscreteAlerts,
  discreteAlertsSearch,
  setDiscreteAlertsSearch,
  loadingDiscreteAlerts,
  onToggleUserAlertStatus,
  onBulkDelete
}) => {
  const ratingLabels = ['', 'MAJOR ISSUES', 'NEEDS ATTENTION', "IT'S OK", 'EXCEPTIONAL'];
  const ratingColors = ['', 'text-red-600', 'text-orange-600', 'text-yellow-600', 'text-green-600'];

  // Filter discrete alerts
  const getFilteredDiscreteAlerts = () => {
    if (!discreteAlertsSearch) return discreteAlerts;
    const term = discreteAlertsSearch.toLowerCase();
    return discreteAlerts.filter(a => 
      a.user_name.toLowerCase().includes(term) ||
      a.user_email.toLowerCase().includes(term) ||
      a.restaurant_name.toLowerCase().includes(term) ||
      a.restaurant_city?.toLowerCase().includes(term)
    );
  };

  const filteredAlerts = getFilteredDiscreteAlerts();

  if (loadingDiscreteAlerts) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading urgent alerts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-3xl text-red-600 mb-4" 
          style={{ 
            fontFamily: 'Bebas Neue, sans-serif',
            letterSpacing: '4px',
            textShadow: 'none',
            fontWeight: '400'
          }}>
        📞 URGENT ALERTS LOG
      </h2>
      
      <p className="text-gray-700 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Complete log of all urgent alerts triggered by users, with full review details and user status.
        Toggle the alert status to enable or disable a user&apos;s ability to send alerts.
      </p>

      {/* Bulk Actions and Search */}
      <div className="flex gap-4 items-center flex-wrap">
        {selectedDiscreteAlerts.length > 0 && (
          <Button
            onClick={onBulkDelete}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 border-3 border-black"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            🗑️ Delete Selected ({selectedDiscreteAlerts.length})
          </Button>
        )}
        <Input
          placeholder="Search by user, email, restaurant, or city..."
          value={discreteAlertsSearch}
          onChange={(e) => setDiscreteAlertsSearch(e.target.value)}
          className="max-w-md border-2 border-black"
        />
        <span className="text-sm text-gray-600">
          Showing {filteredAlerts.length} of {discreteAlerts.length} alerts
        </span>
      </div>

      <div className="bg-white rounded border-2 border-black overflow-x-auto">
        <table className="w-full min-w-[1700px]">
          <thead className="bg-blue-100 border-b-2 border-black sticky top-0">
            <tr>
              <th className="p-3 text-left w-12">
                <Checkbox
                  checked={selectedDiscreteAlerts.length === filteredAlerts.length && filteredAlerts.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedDiscreteAlerts(filteredAlerts.map(a => a.alert_id));
                    } else {
                      setSelectedDiscreteAlerts([]);
                    }
                  }}
                  className="border-blue-800"
                />
              </th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Alert Time</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">User</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Email</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Red Flag</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Alert Status</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Restaurant</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">City</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Phone</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Rating</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Clean</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Supplies</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Maint</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Alert Type</th>
              <th className="p-3 text-left font-bold text-blue-800 text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAlerts.length === 0 ? (
              <tr>
                <td colSpan={15} className="p-8 text-center text-gray-500">
                  No urgent alerts found
                </td>
              </tr>
            ) : (
              filteredAlerts.map((alert, idx) => (
                <tr key={alert.alert_id} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'} ${alert.user_red_flag ? 'bg-red-50' : ''}`}>
                  <td className="p-3">
                    <Checkbox
                      checked={selectedDiscreteAlerts.includes(alert.alert_id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedDiscreteAlerts([...selectedDiscreteAlerts, alert.alert_id]);
                        } else {
                          setSelectedDiscreteAlerts(selectedDiscreteAlerts.filter(id => id !== alert.alert_id));
                        }
                      }}
                    />
                  </td>
                  <td className="p-3 text-gray-600 text-xs">
                    {new Date(alert.triggered_at).toLocaleString()}
                  </td>
                  <td className="p-3 font-semibold text-sm">
                    {alert.user_name}
                    {alert.user_red_flag && <span className="ml-1 text-red-500">🚩</span>}
                  </td>
                  <td className="p-3 text-gray-600 text-xs">{alert.user_email}</td>
                  <td className="p-3">
                    {alert.user_red_flag ? (
                      <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">YES</span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs font-bold rounded">NO</span>
                    )}
                  </td>
                  <td className="p-3">
                    {alert.user_alert_disabled ? (
                      <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">OFF</span>
                    ) : (
                      <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded">ON</span>
                    )}
                  </td>
                  <td className="p-3 text-red-600 font-semibold text-sm max-w-[150px] truncate" title={alert.restaurant_name}>
                    {alert.restaurant_name}
                  </td>
                  <td className="p-3 text-gray-600 text-sm">{alert.restaurant_city}</td>
                  <td className="p-3 text-gray-600 text-xs">{alert.restaurant_phone || '-'}</td>
                  <td className="p-3">
                    <span className={`text-xs font-bold ${ratingColors[alert.review_overall_rating]}`}>
                      {ratingLabels[alert.review_overall_rating] || '-'}
                    </span>
                  </td>
                  <td className="p-3 text-center text-sm">{alert.review_cleanliness || '-'}</td>
                  <td className="p-3 text-center text-sm">{alert.review_supplies || '-'}</td>
                  <td className="p-3 text-center text-sm">{alert.review_maintenance || '-'}</td>
                  <td className="p-3 text-xs text-gray-700">{alert.alert_label || alert.alert_type}</td>
                  <td className="p-3">
                    <Button
                      type="button"
                      onClick={() => onToggleUserAlertStatus(alert.user_id, alert.user_name, alert.user_alert_disabled)}
                      size="sm"
                      className={`text-xs ${alert.user_alert_disabled 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-red-600 hover:bg-red-700 text-white'}`}
                    >
                      {alert.user_alert_disabled ? 'Enable' : 'Disable'}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-sm text-gray-600">
        Total urgent alerts: <strong>{discreteAlerts.length}</strong>
      </div>
    </div>
  );
};
